import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Building2, Plus, RefreshCw, Eye, Trash2, CheckCircle,
  DollarSign, ShoppingCart, Calculator, CreditCard
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Checkbox } from "../../../components/ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const TIPOS_COMPROBANTE = [
  { value: "FC-A", label: "Factura A" },
  { value: "FC-B", label: "Factura B" },
  { value: "FC-C", label: "Factura C" },
  { value: "ND-A", label: "Nota Débito A" },
  { value: "ND-B", label: "Nota Débito B" },
  { value: "NC-A", label: "Nota Crédito A" },
  { value: "NC-B", label: "Nota Crédito B" },
];

const ALICUOTAS_IVA = [
  { value: "21", label: "21%" },
  { value: "10.5", label: "10.5%" },
  { value: "27", label: "27%" },
  { value: "0", label: "0%" },
  { value: "EXENTO", label: "Exento" },
  { value: "NO_GRAVADO", label: "No Gravado" },
];

const Compras = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("facturas");
  const [proveedores, setProveedores] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [tiposRetencion, setTiposRetencion] = useState([]);
  const [tiposPercepcion, setTiposPercepcion] = useState([]);
  
  const [modalProveedor, setModalProveedor] = useState(false);
  const [modalFactura, setModalFactura] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [facturaDetalle, setFacturaDetalle] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formProveedor, setFormProveedor] = useState({
    codigo: "", razon_social: "", nombre_fantasia: "", cuit: "", condicion_iva: "RI", email: "", telefono: "", direccion: ""
  });

  const [formFactura, setFormFactura] = useState({
    proveedor_id: "", tipo_comprobante: "FC-A", punto_venta: "", numero_comprobante: "",
    fecha_comprobante: new Date().toISOString().split("T")[0],
    cae: "", numero_remito: "", proyecto: "", observaciones: "", generar_asiento: true,
    items: [{ descripcion: "", cantidad: 1, precio_unitario: 0, alicuota_iva: "21", centro_costo_id: "", cuenta_contable_id: "" }],
    percepciones: []
  });

  const fetchProveedores = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/proveedores`); setProveedores(r.data); } catch (e) { console.error(e); }
  };
  const fetchFacturas = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/facturas-compra`); setFacturas(r.data); } catch (e) { console.error(e); }
  };
  const fetchCentrosCosto = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/centros-costo`); setCentrosCosto(r.data); } catch (e) { console.error(e); }
  };
  const fetchCuentasContables = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/plan-cuentas?solo_imputables=true`); setCuentasContables(r.data); } catch (e) { console.error(e); }
  };
  const fetchTiposRetencion = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/tipos-retencion`); setTiposRetencion(r.data); } catch (e) { console.error(e); }
  };
  const fetchTiposPercepcion = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/tipos-percepcion`); setTiposPercepcion(r.data); } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProveedores(), fetchFacturas(), fetchCentrosCosto(), fetchCuentasContables(), fetchTiposRetencion(), fetchTiposPercepcion()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSaveProveedor = async () => {
    if (!formProveedor.codigo || !formProveedor.razon_social) { toast.error("Complete campos requeridos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/proveedores`, formProveedor);
      toast.success("Proveedor creado"); setModalProveedor(false); fetchProveedores();
      setFormProveedor({ codigo: "", razon_social: "", nombre_fantasia: "", cuit: "", condicion_iva: "RI", email: "", telefono: "", direccion: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const handleSaveFactura = async () => {
    if (!formFactura.proveedor_id || !formFactura.punto_venta || !formFactura.numero_comprobante) {
      toast.error("Complete proveedor y número de comprobante"); return;
    }
    const itemsValidos = formFactura.items.filter(i => i.descripcion && i.precio_unitario > 0);
    if (itemsValidos.length === 0) { toast.error("Agregue al menos un ítem"); return; }
    
    setSaving(true);
    try {
      const payload = {
        ...formFactura,
        proveedor_id: parseInt(formFactura.proveedor_id),
        items: itemsValidos.map(i => ({
          ...i, cantidad: parseFloat(i.cantidad), precio_unitario: parseFloat(i.precio_unitario),
          centro_costo_id: i.centro_costo_id ? parseInt(i.centro_costo_id) : null,
          cuenta_contable_id: i.cuenta_contable_id ? parseInt(i.cuenta_contable_id) : null
        })),
        percepciones: formFactura.percepciones.map(p => ({
          ...p, tipo_percepcion_id: parseInt(p.tipo_percepcion_id),
          base_calculo: parseFloat(p.base_calculo), alicuota: parseFloat(p.alicuota), monto: parseFloat(p.monto)
        }))
      };
      const res = await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/facturas-compra`, payload);
      toast.success(`Factura creada. Total: ${formatCurrency(res.data.total)}${res.data.asiento_id ? ` - Asiento #${res.data.asiento_id}` : ""}`);
      setModalFactura(false); fetchFacturas();
      setFormFactura({
        proveedor_id: "", tipo_comprobante: "FC-A", punto_venta: "", numero_comprobante: "",
        fecha_comprobante: new Date().toISOString().split("T")[0], cae: "", numero_remito: "", proyecto: "", observaciones: "", generar_asiento: true,
        items: [{ descripcion: "", cantidad: 1, precio_unitario: 0, alicuota_iva: "21", centro_costo_id: "", cuenta_contable_id: "" }], percepciones: []
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear factura"); } finally { setSaving(false); }
  };

  const verDetalle = async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/facturas-compra/${id}`);
      setFacturaDetalle(r.data); setModalDetalle(true);
    } catch (e) { toast.error("Error al cargar detalle"); }
  };

  const addItem = () => setFormFactura({ ...formFactura, items: [...formFactura.items, { descripcion: "", cantidad: 1, precio_unitario: 0, alicuota_iva: "21", centro_costo_id: "", cuenta_contable_id: "" }] });
  const removeItem = (i) => setFormFactura({ ...formFactura, items: formFactura.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, f, v) => { const items = [...formFactura.items]; items[i][f] = v; setFormFactura({ ...formFactura, items }); };

  const addPercepcion = () => setFormFactura({ ...formFactura, percepciones: [...formFactura.percepciones, { tipo_percepcion_id: "", base_calculo: 0, alicuota: 0, monto: 0 }] });
  const removePercepcion = (i) => setFormFactura({ ...formFactura, percepciones: formFactura.percepciones.filter((_, idx) => idx !== i) });
  const updatePercepcion = (i, f, v) => { const percepciones = [...formFactura.percepciones]; percepciones[i][f] = v; setFormFactura({ ...formFactura, percepciones }); };

  const calcularTotales = () => {
    let subtotal = 0, iva = 0;
    formFactura.items.forEach(item => {
      const sub = (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio_unitario) || 0);
      subtotal += sub;
      if (!["EXENTO", "NO_GRAVADO"].includes(item.alicuota_iva)) {
        iva += sub * (parseFloat(item.alicuota_iva) / 100);
      }
    });
    const percepciones = formFactura.percepciones.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
    return { subtotal, iva, percepciones, total: subtotal + iva + percepciones };
  };

  const formatCurrency = (v) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(v || 0);
  const getEstadoBadge = (e) => ({ PENDIENTE: <Badge variant="outline">Pendiente</Badge>, PAGADA: <Badge className="bg-green-500">Pagada</Badge>, PARCIAL: <Badge className="bg-yellow-500">Parcial</Badge>, ANULADA: <Badge className="bg-red-500">Anulada</Badge> }[e] || <Badge>{e}</Badge>);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-blue-500" /></div>;

  const totales = calcularTotales();

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShoppingCart className="w-7 h-7 text-blue-500" />Compras</h1>
          <p className="text-muted-foreground">Gestión de proveedores y facturas de compra</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchFacturas(); fetchProveedores(); toast.success("Actualizado"); }}>
          <RefreshCw className="w-4 h-4 mr-2" />Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Proveedores</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{proveedores.length}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Facturas</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{facturas.length}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Pendientes</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-yellow-600">{facturas.filter(f => f.estado === "PENDIENTE").length}</div></CardContent></Card>
        <Card className="bg-blue-50 dark:bg-blue-950"><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Pendiente</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-blue-600">{formatCurrency(facturas.filter(f => f.estado === "PENDIENTE").reduce((s, f) => s + parseFloat(f.total), 0))}</div></CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="facturas">Facturas de Compra</TabsTrigger>
          <TabsTrigger value="proveedores">Proveedores</TabsTrigger>
        </TabsList>

        <TabsContent value="facturas" className="space-y-4">
          <Button onClick={() => setModalFactura(true)}><Plus className="w-4 h-4 mr-2" />Nueva Factura</Button>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Número</TableHead><TableHead>Proveedor</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Estado</TableHead><TableHead>Asiento</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {facturas.map((fc) => (
                  <TableRow key={fc.id}>
                    <TableCell>{fc.fecha_comprobante}</TableCell>
                    <TableCell><Badge variant="outline">{fc.tipo_comprobante}</Badge></TableCell>
                    <TableCell className="font-mono">{fc.punto_venta}-{fc.numero_comprobante}</TableCell>
                    <TableCell>{fc.proveedor_nombre}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(fc.total)}</TableCell>
                    <TableCell>{getEstadoBadge(fc.estado)}</TableCell>
                    <TableCell>{fc.asiento_id ? <Badge className="bg-green-500">#{fc.asiento_id}</Badge> : <Badge variant="outline">-</Badge>}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => verDetalle(fc.id)}><Eye className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
                {facturas.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No hay facturas</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="proveedores" className="space-y-4">
          <Button onClick={() => setModalProveedor(true)}><Plus className="w-4 h-4 mr-2" />Nuevo Proveedor</Button>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow><TableHead>Código</TableHead><TableHead>Razón Social</TableHead><TableHead>CUIT</TableHead><TableHead>Cond. IVA</TableHead><TableHead>Email</TableHead><TableHead>Teléfono</TableHead></TableRow></TableHeader>
              <TableBody>
                {proveedores.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono">{p.codigo}</TableCell>
                    <TableCell className="font-medium">{p.razon_social}</TableCell>
                    <TableCell>{p.cuit || "-"}</TableCell>
                    <TableCell><Badge variant="outline">{p.condicion_iva}</Badge></TableCell>
                    <TableCell>{p.email || "-"}</TableCell>
                    <TableCell>{p.telefono || "-"}</TableCell>
                  </TableRow>
                ))}
                {proveedores.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No hay proveedores</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Modal Proveedor */}
      <Dialog open={modalProveedor} onOpenChange={setModalProveedor}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuevo Proveedor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Código *</Label><Input value={formProveedor.codigo} onChange={(e) => setFormProveedor({ ...formProveedor, codigo: e.target.value })} placeholder="PROV-001" /></div>
            <div className="space-y-2"><Label>CUIT</Label><Input value={formProveedor.cuit} onChange={(e) => setFormProveedor({ ...formProveedor, cuit: e.target.value })} placeholder="30-12345678-9" /></div>
            <div className="col-span-2 space-y-2"><Label>Razón Social *</Label><Input value={formProveedor.razon_social} onChange={(e) => setFormProveedor({ ...formProveedor, razon_social: e.target.value })} placeholder="Nombre de la empresa" /></div>
            <div className="space-y-2"><Label>Condición IVA</Label>
              <Select value={formProveedor.condicion_iva} onValueChange={(v) => setFormProveedor({ ...formProveedor, condicion_iva: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="RI">Resp. Inscripto</SelectItem>
                  <SelectItem value="MO">Monotributista</SelectItem>
                  <SelectItem value="EX">Exento</SelectItem>
                  <SelectItem value="CF">Cons. Final</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={formProveedor.email} onChange={(e) => setFormProveedor({ ...formProveedor, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Teléfono</Label><Input value={formProveedor.telefono} onChange={(e) => setFormProveedor({ ...formProveedor, telefono: e.target.value })} /></div>
            <div className="space-y-2"><Label>Dirección</Label><Input value={formProveedor.direccion} onChange={(e) => setFormProveedor({ ...formProveedor, direccion: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalProveedor(false)}>Cancelar</Button><Button onClick={handleSaveProveedor} disabled={saving}>{saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nueva Factura */}
      <Dialog open={modalFactura} onOpenChange={setModalFactura}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Nueva Factura de Compra</DialogTitle></DialogHeader>
          <div className="space-y-6">
            {/* Cabecera */}
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2 space-y-2"><Label>Proveedor *</Label>
                <Select value={formFactura.proveedor_id} onValueChange={(v) => setFormFactura({ ...formFactura, proveedor_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
                  <SelectContent>{proveedores.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.codigo} - {p.razon_social}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Tipo *</Label>
                <Select value={formFactura.tipo_comprobante} onValueChange={(v) => setFormFactura({ ...formFactura, tipo_comprobante: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_COMPROBANTE.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Fecha *</Label><Input type="date" value={formFactura.fecha_comprobante} onChange={(e) => setFormFactura({ ...formFactura, fecha_comprobante: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2"><Label>Punto de Venta *</Label><Input value={formFactura.punto_venta} onChange={(e) => setFormFactura({ ...formFactura, punto_venta: e.target.value })} placeholder="00001" maxLength={5} /></div>
              <div className="space-y-2"><Label>Número *</Label><Input value={formFactura.numero_comprobante} onChange={(e) => setFormFactura({ ...formFactura, numero_comprobante: e.target.value })} placeholder="00000001" maxLength={8} /></div>
              <div className="space-y-2"><Label>CAE</Label><Input value={formFactura.cae} onChange={(e) => setFormFactura({ ...formFactura, cae: e.target.value })} /></div>
              <div className="space-y-2"><Label>Nº Remito</Label><Input value={formFactura.numero_remito} onChange={(e) => setFormFactura({ ...formFactura, numero_remito: e.target.value })} /></div>
            </div>

            {/* Items */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3"><Label className="text-base font-semibold">Detalle de Ítems</Label><Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-4 h-4 mr-1" />Agregar</Button></div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
                  <div className="col-span-3">Descripción</div><div className="col-span-1">Cant.</div><div className="col-span-2">Precio</div><div className="col-span-1">IVA</div><div className="col-span-2">Centro Costo</div><div className="col-span-2">Cuenta</div><div className="col-span-1"></div>
                </div>
                {formFactura.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 bg-muted/50 rounded">
                    <div className="col-span-3"><Input value={item.descripcion} onChange={(e) => updateItem(i, "descripcion", e.target.value)} placeholder="Descripción" /></div>
                    <div className="col-span-1"><Input type="number" min="0" step="0.01" value={item.cantidad} onChange={(e) => updateItem(i, "cantidad", e.target.value)} /></div>
                    <div className="col-span-2"><Input type="number" min="0" step="0.01" value={item.precio_unitario} onChange={(e) => updateItem(i, "precio_unitario", e.target.value)} /></div>
                    <div className="col-span-1">
                      <Select value={item.alicuota_iva} onValueChange={(v) => updateItem(i, "alicuota_iva", v)}>
                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>{ALICUOTAS_IVA.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="col-span-2">
                      <Select value={item.centro_costo_id} onValueChange={(v) => updateItem(i, "centro_costo_id", v)}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="CC" /></SelectTrigger>
                        <SelectContent>{centrosCosto.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.codigo}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="col-span-2">
                      <Select value={item.cuenta_contable_id} onValueChange={(v) => updateItem(i, "cuenta_contable_id", v)}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Cuenta" /></SelectTrigger>
                        <SelectContent>{cuentasContables.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.codigo}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="col-span-1">{formFactura.items.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-destructive" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4" /></Button>}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Percepciones */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3"><Label className="text-base font-semibold">Percepciones</Label><Button type="button" variant="outline" size="sm" onClick={addPercepcion}><Plus className="w-4 h-4 mr-1" />Agregar</Button></div>
              {formFactura.percepciones.length > 0 && (
                <div className="space-y-2">
                  {formFactura.percepciones.map((p, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 items-center p-2 bg-muted/50 rounded">
                      <Select value={p.tipo_percepcion_id} onValueChange={(v) => updatePercepcion(i, "tipo_percepcion_id", v)}>
                        <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>{tiposPercepcion.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.nombre}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" placeholder="Base" value={p.base_calculo} onChange={(e) => updatePercepcion(i, "base_calculo", e.target.value)} />
                      <Input type="number" placeholder="%" value={p.alicuota} onChange={(e) => updatePercepcion(i, "alicuota", e.target.value)} />
                      <Input type="number" placeholder="Monto" value={p.monto} onChange={(e) => updatePercepcion(i, "monto", e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => removePercepcion(i)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Totales y opciones */}
            <div className="border-t pt-4 grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="generar_asiento" checked={formFactura.generar_asiento} onCheckedChange={(c) => setFormFactura({ ...formFactura, generar_asiento: c })} />
                <Label htmlFor="generar_asiento">Generar asiento contable automático</Label>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg space-y-2">
                <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">{formatCurrency(totales.subtotal)}</span></div>
                <div className="flex justify-between"><span>IVA:</span><span className="font-medium">{formatCurrency(totales.iva)}</span></div>
                {totales.percepciones > 0 && <div className="flex justify-between"><span>Percepciones:</span><span className="font-medium">{formatCurrency(totales.percepciones)}</span></div>}
                <div className="flex justify-between text-lg font-bold border-t pt-2"><span>TOTAL:</span><span className="text-blue-600">{formatCurrency(totales.total)}</span></div>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalFactura(false)}>Cancelar</Button><Button onClick={handleSaveFactura} disabled={saving}>{saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Guardar Factura</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalle Factura */}
      <Dialog open={modalDetalle} onOpenChange={setModalDetalle}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detalle de Factura</DialogTitle></DialogHeader>
          {facturaDetalle && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div><p className="text-sm text-muted-foreground">Proveedor</p><p className="font-semibold">{facturaDetalle.proveedor_nombre}</p></div>
                <div><p className="text-sm text-muted-foreground">Comprobante</p><p className="font-semibold">{facturaDetalle.tipo_comprobante} {facturaDetalle.punto_venta}-{facturaDetalle.numero_comprobante}</p></div>
                <div><p className="text-sm text-muted-foreground">Fecha</p><p className="font-semibold">{facturaDetalle.fecha_comprobante}</p></div>
                <div><p className="text-sm text-muted-foreground">Total</p><p className="text-xl font-bold text-blue-600">{formatCurrency(facturaDetalle.total)}</p></div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Descripción</TableHead><TableHead className="text-right">Cant.</TableHead><TableHead className="text-right">P.Unit.</TableHead><TableHead className="text-right">IVA</TableHead><TableHead>Centro Costo</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader>
                <TableBody>
                  {facturaDetalle.items?.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.descripcion}</TableCell>
                      <TableCell className="text-right">{item.cantidad}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.precio_unitario)}</TableCell>
                      <TableCell className="text-right">{item.alicuota_iva}%</TableCell>
                      <TableCell><Badge variant="outline">{item.centro_costo_nombre || "-"}</Badge></TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.subtotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {facturaDetalle.asiento_id && <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /><span>Asiento contable #{facturaDetalle.asiento_id} generado</span></div>}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setModalDetalle(false)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Compras;
